import React, { useState, useEffect } from 'react';
import { X, Search } from 'lucide-react';
import { useSettings } from './contexts/SettingsContext';
import { useAuth } from './contexts/AuthContext';
import apiService from './apiService';

const AddAppointmentView = ({ onAddAppointment, onCancel }) => {
    const { appSettings } = useSettings();
    const { currentUser } = useAuth();
    const [currentStaffId, setCurrentStaffId] = useState(null);
    const [doctors, setDoctors] = useState([]);
    const [patients, setPatients] = useState([]);
    const [patientSearchTerm, setPatientSearchTerm] = useState('');
    const [showPatientDropdown, setShowPatientDropdown] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const isDoctor = currentUser?.role === 'doctor';
    
    const [formData, setFormData] = useState({
        patientId: '',
        doctorId: '',
        appointmentTime: new Date().toISOString().slice(0, 16),
        department: '',
        appointmentType: '',
        status: 'scheduled',
        notes: '',
        duration: '',
        priority: '',
        isRecurring: false,
        recurrencePattern: '',
        organizationId: appSettings.id,
    });
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);

    // Fetch all doctors, patients and set current staff ID if user is a doctor
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch staff (doctors)
                const staff = await apiService.getStaff();
                setDoctors(staff);

                // Fetch patients
                const patientData = await apiService.getPatients();
                setPatients(patientData);

                // If current user is a doctor, auto-select them
                if (isDoctor && currentUser?.id) {
                    const currentStaff = staff.find(s => s.userId === currentUser.id);
                    if (currentStaff) {
                        setCurrentStaffId(currentStaff.id);
                        setFormData(prev => ({ ...prev, doctorId: currentStaff.id.toString() }));
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchData();
    }, [isDoctor, currentUser]);

    // Filter patients based on search term
    const filteredPatients = patients.filter(patient => {
        if (!patientSearchTerm) return false;
        const searchLower = patientSearchTerm.toLowerCase();
        return (
            patient.name?.toLowerCase().includes(searchLower) ||
            patient.patientId?.toLowerCase().includes(searchLower) ||
            patient.phone?.includes(searchLower)
        );
    });

    const handlePatientSearch = (e) => {
        const value = e.target.value;
        setPatientSearchTerm(value);
        setShowPatientDropdown(value.length > 0);
        
        // Clear selection if user is typing
        if (selectedPatient) {
            setSelectedPatient(null);
            setFormData(prev => ({ ...prev, patientId: '' }));
        }
    };

    const handlePatientSelect = (patient) => {
        console.log('Selected patient:', patient);
        setSelectedPatient(patient);
        setPatientSearchTerm(`${patient.name} - ${patient.patientId}`);
        setFormData(prev => ({ 
            ...prev, 
            patientId: patient.patientId  // Use custom patient ID (e.g., "78303196"), not database ID
        }));
        setShowPatientDropdown(false);
        
        // Clear validation error for this field
        if (validationErrors.patientId) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.patientId;
                return newErrors;
            });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: name === 'isRecurring' ? value === 'true' : value,
        }));
    };

    const validateForm = () => {
        const errors = {};
        
        console.log('Validating form. PatientId:', formData.patientId);
        
        // Validate patient selection - only check formData.patientId
        if (!formData.patientId || formData.patientId === '') {
            console.log('Patient validation FAILED - patientId is empty');
            errors.patientId = 'Please select a patient';
        } else {
            console.log('Patient validation PASSED - patientId:', formData.patientId);
        }
        
        // Validate doctor selection (only for non-doctors)
        if (!isDoctor && (!formData.doctorId || formData.doctorId === '')) {
            errors.doctorId = 'Please select a doctor';
        }
        
        // Validate appointment time
        if (!formData.appointmentTime || formData.appointmentTime === '') {
            errors.appointmentTime = 'Please select date and time';
        }
        
        // Validate appointment type
        if (!formData.appointmentType || formData.appointmentType.trim() === '') {
            errors.appointmentType = 'Please select appointment type';
        }
        
        setValidationErrors(errors);
        console.log('Total validation errors:', errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setAttemptedSubmit(true);
        setError(null);

        console.log('Form data at submit:', formData);
        console.log('Selected patient:', selectedPatient);
        
        // Validate form
        if (!validateForm()) {
            console.log('Validation errors:', validationErrors);
            setError('Please fill in all required fields marked in red');
            return;
        }

        const dataToSend = {
            patientId: formData.patientId,  // Keep as string (custom patient ID like "78303196")
            doctorId: parseInt(formData.doctorId),
            appointmentTime: formData.appointmentTime,
            department: formData.department || null,
            appointmentType: formData.appointmentType || null,
            status: formData.status,
            notes: formData.notes || null,
            duration: formData.duration ? parseInt(formData.duration) : null,
            priority: formData.priority || null,
            isRecurring: formData.isRecurring,
            recurrencePattern: formData.recurrencePattern || null,
            organizationId: formData.organizationId,
        };

        console.log('Sending appointment data:', dataToSend);

        try {
            await onAddAppointment(dataToSend);
            // Reset form
            setFormData({
                patientId: '',
                doctorId: isDoctor && currentStaffId ? currentStaffId.toString() : '', // Keep doctor ID for doctors
                appointmentTime: new Date().toISOString().slice(0, 16),
                department: '',
                appointmentType: '',
                status: 'scheduled',
                notes: '',
                duration: '',
                priority: '',
                isRecurring: false,
                recurrencePattern: '',
                organizationId: appSettings.id,
            });
            // Reset patient search
            setPatientSearchTerm('');
            setSelectedPatient(null);
            setShowPatientDropdown(false);
        } catch (error) {
            console.error('Appointment creation failed:', error);
            setError('Failed to create appointment. Please try again.');
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-800">Schedule New Appointment</h3>
                <button onClick={onCancel} className="text-gray-500 hover:text-gray-700" title="Close">
                    <X className="w-6 h-6" />
                </button>
            </div>
            {error && <div className="p-3 mb-4 text-red-600 bg-red-100 rounded-md">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                    <label htmlFor="patientSearch" className="block text-sm font-medium text-gray-700">
                        Search Patient <span className="text-red-500">*</span>
                    </label>
                    <div className="relative mt-1">
                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${
                            attemptedSubmit && validationErrors.patientId ? 'text-red-400' : 'text-gray-400'
                        }`} />
                        <input
                            type="text"
                            id="patientSearch"
                            value={patientSearchTerm}
                            onChange={handlePatientSearch}
                            onFocus={() => patientSearchTerm && setShowPatientDropdown(true)}
                            onBlur={() => setTimeout(() => setShowPatientDropdown(false), 200)}
                            placeholder="Type patient name, ID, or phone..."
                            autoComplete="off"
                            className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none sm:text-sm ${
                                attemptedSubmit && validationErrors.patientId
                                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50'
                                    : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                            }`}
                        />
                        {selectedPatient && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    ✓ Selected
                                </span>
                            </div>
                        )}
                    </div>
                    {attemptedSubmit && validationErrors.patientId && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <span className="font-bold">!</span> {validationErrors.patientId}
                        </p>
                    )}
                    
                    {/* Autocomplete Dropdown */}
                    {showPatientDropdown && filteredPatients.length > 0 && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                            {filteredPatients.slice(0, 10).map((patient) => (
                                <button
                                    key={patient.id}
                                    type="button"
                                    onMouseDown={(e) => {
                                        e.preventDefault(); // Prevent blur from firing
                                        handlePatientSelect(patient);
                                    }}
                                    className="w-full px-4 py-2 text-left hover:bg-blue-50 focus:bg-blue-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                                >
                                    <div className="font-medium text-gray-900">{patient.name}</div>
                                    <div className="text-sm text-gray-600">
                                        ID: {patient.patientId} {patient.phone && `• ${patient.phone}`} {patient.age && `• ${patient.age}yr`}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                    
                    {/* No results message */}
                    {showPatientDropdown && patientSearchTerm && filteredPatients.length === 0 && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg p-4">
                            <p className="text-sm text-gray-600">
                                No patients found matching "<strong>{patientSearchTerm}</strong>"
                            </p>
                        </div>
                    )}
                    
                    <p className="mt-1 text-xs text-gray-500">
                        💡 Type patient name, patient ID, or phone number to search
                    </p>
                </div>
                
                {/* Doctor Selection - Only show for non-doctors (receptionist/admin) */}
                {!isDoctor && (
                    <div>
                        <label htmlFor="doctorId" className="block text-sm font-medium text-gray-700">
                            Select Doctor <span className="text-red-500">*</span>
                        </label>
                        <div className="relative mt-1">
                            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${
                                attemptedSubmit && validationErrors.doctorId ? 'text-red-400' : 'text-gray-400'
                            }`} />
                            <select
                                id="doctorId"
                                name="doctorId"
                                value={formData.doctorId}
                                onChange={handleChange}
                                className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm focus:outline-none sm:text-sm appearance-none ${
                                    attemptedSubmit && validationErrors.doctorId
                                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50'
                                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                }`}
                            >
                                <option value="">-- Select a Doctor --</option>
                                {doctors.map((doctor) => (
                                    <option key={doctor.id} value={doctor.id}>
                                        {doctor.name || doctor.fullName} {doctor.specialization ? `- ${doctor.specialization}` : ''} {doctor.department ? `(${doctor.department})` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {attemptedSubmit && validationErrors.doctorId && (
                            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                                <span className="font-bold">!</span> {validationErrors.doctorId}
                            </p>
                        )}
                        {doctors.length === 0 && !validationErrors.doctorId && (
                            <p className="mt-1 text-xs text-amber-600">
                                ⚠️ No doctors found. Please add staff members with doctor role first.
                            </p>
                        )}
                    </div>
                )}
                
                {/* Show doctor's info for doctors */}
                {isDoctor && currentStaffId && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-800">
                            <strong>Doctor:</strong> {currentUser?.username}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                            ✓ This appointment will be assigned to you automatically
                        </p>
                    </div>
                )}
                
                <div>
                    <label htmlFor="appointmentTime" className="block text-sm font-medium text-gray-700">
                        Appointment Date & Time <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="datetime-local"
                        id="appointmentTime"
                        name="appointmentTime"
                        value={formData.appointmentTime}
                        onChange={handleChange}
                        className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none sm:text-sm ${
                            attemptedSubmit && validationErrors.appointmentTime
                                ? 'border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50'
                                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                    />
                    {attemptedSubmit && validationErrors.appointmentTime && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <span className="font-bold">!</span> {validationErrors.appointmentTime}
                        </p>
                    )}
                </div>
                <div>
                    <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                        Department
                    </label>
                    <input
                        type="text"
                        id="department"
                        name="department"
                        value={formData.department}
                        onChange={handleChange}
                        placeholder="e.g., Cardiology, Neurology"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                </div>
                <div>
                    <label htmlFor="appointmentType" className="block text-sm font-medium text-gray-700">
                        Appointment Type <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="appointmentType"
                        name="appointmentType"
                        value={formData.appointmentType}
                        onChange={handleChange}
                        placeholder="e.g., General Consultation, Follow-up, Check-up"
                        className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none sm:text-sm ${
                            attemptedSubmit && validationErrors.appointmentType
                                ? 'border-red-500 focus:ring-red-500 focus:border-red-500 bg-red-50'
                                : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        }`}
                    />
                    {attemptedSubmit && validationErrors.appointmentType && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                            <span className="font-bold">!</span> {validationErrors.appointmentType}
                        </p>
                    )}
                </div>
                <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                        Status
                    </label>
                    <select
                        id="status"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                        <option value="scheduled">Scheduled</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                </div>
                <div>
                    <label htmlFor="duration" className="block text-sm font-medium text-gray-700">
                        Duration (minutes)
                    </label>
                    <input
                        type="number"
                        id="duration"
                        name="duration"
                        value={formData.duration}
                        onChange={handleChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                </div>
                <div>
                    <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
                        Priority
                    </label>
                    <input
                        type="text"
                        id="priority"
                        name="priority"
                        value={formData.priority}
                        onChange={handleChange}
                        placeholder="e.g., High, Medium, Low"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                </div>
                <div>
                    <label htmlFor="isRecurring" className="block text-sm font-medium text-gray-700">
                        Is Recurring
                    </label>
                    <select
                        id="isRecurring"
                        name="isRecurring"
                        value={formData.isRecurring}
                        onChange={handleChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                    </select>
                </div>
                <div>
                    <label htmlFor="recurrencePattern" className="block text-sm font-medium text-gray-700">
                        Recurrence Pattern (e.g., Weekly)
                    </label>
                    <input
                        type="text"
                        id="recurrencePattern"
                        name="recurrencePattern"
                        value={formData.recurrencePattern}
                        onChange={handleChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                </div>
                <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                        Notes (Optional)
                    </label>
                    <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Additional notes about the appointment"
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    ></textarea>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                        Schedule Appointment
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddAppointmentView;